import Component from '@glimmer/component';
import { service } from '@ember/service';
import { TrackedAsyncData } from 'ember-async-data';
import { cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import scrollIntoView from '../../modifiers/scroll-into-view';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import t from 'ember-intl/helpers/t';
import hasManyLength from '../../helpers/has-many-length';
import { hash } from '@ember/helper';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { faArrowRotateLeft, faLinkSlash } from '@fortawesome/free-solid-svg-icons';

export default class CoursePublicationCheckComponent extends Component {
  @service router;
  @service intl;
  @service flashMessages;

  @cached
  get courseObjectives() {
    return new TrackedAsyncData(this.args.course.courseObjectives);
  }

  @cached
  get showUnlinkIcon() {
    if (!this.courseObjectives.isResolved) {
      return false;
    }
    const objectivesWithoutParents = this.courseObjectives.value.filter((objective) => {
      return objective.programYearObjectives.length === 0;
    });

    return objectivesWithoutParents.length > 0;
  }

  get hasMissingRequirements() {
    return this.args.course.requiredPublicationIssues.length !== 0;
  }

  get hasMissingItems() {
    return this.args.course.allPublicationIssuesLength !== 0;
  }

  @action
  async publish() {
    this.args.course.set('publishedAsTbd', false);
    this.args.course.set('published', true);
    await this.args.course.save();
    this.flashMessages.success(this.intl.t('general.publishedSuccessfully'));
    this.router.transitionTo('course', this.args.course);
  }

  <template>
    <div
      class="course-publicationcheck main-section"
      data-test-course-publicationcheck
      {{scrollIntoView delay=10}}
    >
      <div class="back-to-course">
        <LinkTo @route="course" @model={{@course}} data-test-back-to-course>
          <FaIcon @icon={{faArrowRotateLeft}} />
          {{t "general.backToTitle" title=@course.title}}
        </LinkTo>
      </div>

      <section class="course-publicationcheck-details">
        <h3 class="title" data-test-title>{{t "general.publicationReview"}}</h3>
        <div class="sub-title" data-test-missing-items>
          {{t "general.missingItems"}}
          ({{@course.allPublicationIssuesLength}})
        </div>
        <div class="course-publicationcheck-content">
          <table class="ilios-table">
            <thead>
              <tr>
                <th>
                  {{t "general.courseTitle"}}
                </th>
                <th>
                  {{t "general.cohorts"}}
                </th>
                <th>
                  {{t "general.terms"}}
                </th>
                <th>
                  {{t "general.objectives"}}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td data-test-course-title>
                  {{@course.title}}
                </td>
                {{#if @course.cohorts.length}}
                  <td class="yes" data-test-cohorts>
                    {{t "general.yes"}}
                    ({{@course.cohorts.length}})
                  </td>
                {{else}}
                  <td class="no" data-test-cohorts>
                    {{t "general.no"}}
                  </td>
                {{/if}}
                {{#if @course.terms.length}}
                  <td class="yes" data-test-terms>
                    {{t "general.yes"}}
                    ({{@course.terms.length}})
                  </td>
                {{else}}
                  <td class="no" data-test-terms>
                    {{t "general.no"}}
                  </td>
                {{/if}}
                {{#if (hasManyLength @course "courseObjectives")}}
                  <td class="yes" data-test-objectives>
                    {{t "general.yes"}}
                    ({{hasManyLength @course "courseObjectives"}})
                    {{#if this.showUnlinkIcon}}
                      <LinkTo
                        @route="course"
                        @model={{@course}}
                        @query={{hash details=true courseObjectiveDetails=true}}
                        aria-label={{t "general.backToCourse"}}
                        data-test-unlink
                      >
                        <FaIcon @icon={{faLinkSlash}} />
                      </LinkTo>
                    {{/if}}
                  </td>
                {{else}}
                  <td class="no" data-test-objectives>
                    {{t "general.no"}}
                  </td>
                {{/if}}
              </tr>
            </tbody>
          </table>
        </div>
        <div data-test-course-publicationcheck-actions>
          {{#if this.hasMissingRequirements}}
            <button
              type="button"
              disabled
              title="{{t 'general.canNotPublishCourse'}}"
              data-test-publish-missing-requirements
            >
              {{t "general.publishCourse"}}
            </button>
          {{else if this.hasMissingItems}}
            <button type="button" {{on "click" this.publish}} data-test-publish-with-missing-items>
              {{t
                "general.publishCourseWithMissingItems"
                missingItemCount=@course.allPublicationIssuesLength
              }}
            </button>
          {{else}}
            <button type="button" {{on "click" this.publish}} data-test-publish>
              {{t "general.publishCourse"}}
            </button>
          {{/if}}
        </div>
      </section>
    </div>
  </template>
}
